import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { MatTableDataSource as MatTableDataSource } from "@angular/material/table";
import { OnInit } from "@angular/core";
import { Subject } from "rxjs";
import { debounceTime, takeUntil, distinctUntilChanged } from "rxjs/operators";

@Component({
  selector: "app-search-text",
  templateUrl: "./search-text.component.html",
  styleUrls: ["./search-text.component.scss"],
  standalone: false,
})
export class SearchTextComponent implements OnInit, OnDestroy, OnChanges {
  @Input() dataSource: MatTableDataSource<any>;
  @Input() setPredicate = true;
  @Input() text = "Search";
  @Output() search: EventEmitter<string> = new EventEmitter<string>();
  searchText = "";
  searchSubject: Subject<string> = new Subject();
  destroyed$ = new Subject<boolean>();

  ngOnInit(): void {
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroyed$),
      )
      .subscribe((filterValue) => this.applyFilter(filterValue));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If the table datasource is recreated (common after reloads),
    // re-apply the current search so the UI and filtered results stay in sync.
    if (changes["dataSource"] && this.dataSource && this.searchText) {
      this.applyFilter(this.searchText);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  keyUp(event: Event) {
    const filterValue = (event.target as HTMLInputElement)?.value ?? "";
    this.searchSubject.next(filterValue);
  }

  clear() {
    this.searchText = "";
    this.applyFilter("");
  }

  applyFilter(filterValue: string) {
    // If a datasource is provided, and the consumer allows it, update its filterPredicate
    if (this.dataSource) {
      if (this.setPredicate) {
        this.dataSource.filterPredicate = (data, filter: string) => {
          const accumulator = (currentTerm, key) => {
            return this.nestedFilterCheck(currentTerm, data, key);
          };
          const dataStr = Object.keys(data)
            .reduce(accumulator, "")
            .toLowerCase();
          const transformedFilter = filter.trim().toLowerCase();
          return dataStr.indexOf(transformedFilter) !== -1;
        };
      }

      this.dataSource.filter = filterValue.trim().toLowerCase();

      if (this.dataSource.paginator) {
        this.dataSource.paginator.firstPage();
      }
    }

    // Emit the raw filter value for consumers that want to react (without providing a dataSource)
    this.search.emit(filterValue);
  }

  nestedFilterCheck(search, data, key) {
    if (typeof data[key] === "object") {
      for (const k in data[key]) {
        if (data[key][k] !== null) {
          search = this.nestedFilterCheck(search, data[key], k);
        }
      }
    } else {
      search += data[key];
    }
    return search;
  }
}
